import { useRollbar } from '@rollbar/react'
import { useState } from 'react'
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { twMerge } from 'tailwind-merge'
import Confirmation from 'shared/components/confirmation-modal'
import useUser, { isDomainOwner, isWsAdmin, isWsAssistant } from 'shared/hooks/use-user'
import { useRemoveTopic } from 'modules/community/api/use-remove-topic'
import { useUpdateTopicsOrder } from 'modules/community/api/use-update-topics-order'
import { useCommunityWithTopics } from 'modules/community/hooks/use-community-with-topics'
import { CommunityWithTopicsInterface } from 'modules/community/types/community-interface'
import { TopicInterface } from 'modules/community/types/topic-interface'
import UpdateTopic from '../../update-topic'
import { TopicItem } from './topic-item'
import { TopicSortableWrapper } from './topic-sortable-wrapper'

export const TopicList = ({ topicItemClassName }: { topicItemClassName?: string }) => {
  const { t } = useTranslation()
  const { user } = useUser()
  const rollbar = useRollbar()
  const router = useRouter()
  const { communityPath, topicPath } = router.query as { communityPath: string; topicPath?: string }

  const [editableTopic, setEditableTopic] = useState<TopicInterface | null>(null)
  const [removableTopicId, setRemovableTopicId] = useState<number | null>(null)
  const [activeId, setActiveId] = useState<number | null>(null)
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({})

  const getCommunityWithTopicsQuery = useCommunityWithTopics()
  const updateTopicsOrderMutation = useUpdateTopicsOrder({ communityPath })
  const removeTopicMutation = useRemoveTopic({ topicId: removableTopicId })

  const topics = getCommunityWithTopicsQuery.data?.topics
  const selectedTopic = getCommunityWithTopicsQuery.data?.topics.find(
    topic => topic.path === topicPath,
  )
  const canManageTopics = user && (isDomainOwner(user) || isWsAdmin(user) || isWsAssistant(user))

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id as number)
    const node = document.querySelector<HTMLElement>(`[data-topic-id='${active.id}']`)
    if (node) {
      const { width, height } = node.getBoundingClientRect()
      setOverlayStyle({ width, height })
    }
  }

  const handleDragEnd = (event: DragEndEvent) => {
    if (!getCommunityWithTopicsQuery.data) return
    setActiveId(null)
    const { active, over } = event

    if (active.id !== over?.id) {
      const prevTopics = [...getCommunityWithTopicsQuery.data.topics]
      const oldIndex = prevTopics.findIndex(t => t.id === active.id)
      const newIndex = prevTopics.findIndex(t => t.id === over?.id)
      const newTopics = arrayMove(prevTopics, oldIndex, newIndex)

      getCommunityWithTopicsQuery.mutate(
        { ...getCommunityWithTopicsQuery.data, topics: newTopics },
        false,
      )

      updateTopicsOrderMutation
        .trigger({ orderedTopicIds: newTopics.map(topic => topic.id) })
        .then(data => {
          getCommunityWithTopicsQuery.data &&
            getCommunityWithTopicsQuery.mutate(
              {
                ...getCommunityWithTopicsQuery.data,
                topics: data,
              } satisfies CommunityWithTopicsInterface,
              false,
            )
        })
        .catch(error => {
          getCommunityWithTopicsQuery.data &&
            getCommunityWithTopicsQuery.mutate(
              {
                ...getCommunityWithTopicsQuery.data!,
                topics: prevTopics,
              } satisfies CommunityWithTopicsInterface,
              false,
            )
          rollbar.error(error)
        })
    }
  }

  const handleRemoveTopic = async () => {
    if (!getCommunityWithTopicsQuery.data || !removableTopicId) return
    await removeTopicMutation.trigger()
    const newTopics = getCommunityWithTopicsQuery.data.topics.filter(t => t.id !== removableTopicId)
    setRemovableTopicId(null)
    await getCommunityWithTopicsQuery.mutate(
      { ...getCommunityWithTopicsQuery.data, topics: newTopics },
      false,
    )
  }

  if (!topics) return null

  const renderTopics = () => {
    const activeTopic = topics.find(t => t.id === activeId)

    if (canManageTopics) {
      return (
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            strategy={verticalListSortingStrategy}
            items={topics.map(topic => topic.id)}
          >
            <ul className="max-h-80 overflow-auto py-1">
              {topics.map(topic => (
                <TopicSortableWrapper
                  key={topic.path}
                  id={topic.id}
                  as="li"
                  data-topic-id={topic.id}
                  className={twMerge(
                    'group flex w-full touch-none items-center justify-between py-1 pl-1 pr-2',
                    topicItemClassName,
                  )}
                >
                  <TopicItem
                    topic={topic}
                    isSelected={selectedTopic?.id === topic.id}
                    communityPath={communityPath}
                    onEdit={setEditableTopic}
                    onDelete={setRemovableTopicId}
                  />
                </TopicSortableWrapper>
              ))}
            </ul>
          </SortableContext>
          <DragOverlay adjustScale={false}>
            {activeTopic ? (
              <div
                className="group flex items-center justify-between rounded bg-transparent p-1"
                style={overlayStyle}
              >
                <TopicItem
                  topic={activeTopic}
                  isSelected={selectedTopic?.id === activeTopic.id}
                  communityPath={communityPath}
                  onEdit={setEditableTopic}
                  onDelete={setRemovableTopicId}
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )
    }

    return (
      <ul className="max-h-80 overflow-auto py-2">
        {topics.map(topic => (
          <li
            key={topic.path}
            className={twMerge(
              'group flex w-full items-center justify-between px-4 py-1',
              topicItemClassName,
            )}
          >
            <TopicItem
              topic={topic}
              isSelected={selectedTopic?.id === topic.id}
              communityPath={communityPath}
              onEdit={setEditableTopic}
              onDelete={setRemovableTopicId}
            />
          </li>
        ))}
      </ul>
    )
  }

  return (
    <>
      {renderTopics()}
      {editableTopic && (
        <UpdateTopic
          topic={editableTopic}
          onCloseModal={() => setEditableTopic(null)}
          onUpdateSuccess={() => setEditableTopic(null)}
        />
      )}
      {removableTopicId && (
        <Confirmation
          onConfirm={handleRemoveTopic}
          onCloseModal={() => setRemovableTopicId(null)}
          confirmationContent={t('settings.form.delete_topic_confirmation.label')}
        />
      )}
    </>
  )
}
